<template>
  <div class="container long-grass-dispute">
    <BLoading :active.sync="longGrass.loadingStatus.isLoading" />

    <div v-if="!longGrass.loadingStatus.isLoading" class="columns">
      <main class="column is-6">
        <header>
          <LsSupportHeader label="Long Grass Dispute" />
          <p v-if="completedOn">Service completed on {{ completedOn }}</p>
        </header>
        <form novalidate @submit.prevent="submitForm">
          <div class="ctrl-group">
            <LsSelect
              v-model="form.reason"
              label="Why are you disputing the report?"
              track-id="longgrass_dispute-reason_select"
              :options="disputeReasonsList"
              name="reason-select"
              placeholder="-Select-"
              :validation="{ required: true }"
            />
          </div>
          <div v-if="disputeReasonDetailsList.length > 0" class="ctrl-group">
            <LsSelect
              v-model="form.reasonDetails"
              :label="disputeReasonDetailsLabel"
              track-id="longgrass_dispute-reason_details_select"
              test-id="dispute_reason_details_select"
              :options="disputeReasonDetailsList"
              name="reason-details-select"
              placeholder="-Select-"
              :validation="{ required: true }"
            />
          </div>
          <div v-if="showDetailsTextarea" class="ctrl-group">
            <LsInput
              v-model="form.reasonComments"
              type="textarea"
              label="Share more details"
              track-id="longgrass_dispute-details_textarea"
              placeholder="Enter details here..."
              name="reason"
              maxlength="512"
              :validation="{ required: additionalDetailsRequired }"
            />
          </div>
          <div v-if="isReasonCustomerUnawareOfPolicy">
            <LsInfoCard test-id="customer_unaware_infocard">
              <p><strong>How does long grass pricing work?</strong></p>
              <p>
                The
                <RouterLink :to="{ name: 'LongGrassPolicy' }">Long Grass Policy</RouterLink>
                has to be reviewed and accepted by all customers during sign up.
              </p>
              <p>
                <RouterLink :to="{ name: 'longGrassPricing' }"
                  >How does long grass pricing work?</RouterLink
                >
              </p>
              <p>
                If you have questions about this, please contact our
                <RouterLink :to="{ name: 'messageLS' }">support team</RouterLink>.
              </p>
            </LsInfoCard>
            <LsButton
              text="Exit Long Grass dispute"
              track-id="longgrass_dispute-cancel_button"
              test-id="cancel_dispute_button"
              bg-color="primary-light"
              text-color="primary"
              @click="goToReport"
            />
          </div>
          <div v-if="showSubmitButton" class="ctrl-group submit">
            <LsButton
              type="submit"
              test-id="submit_dispute_button"
              text="Submit"
              bg-color="primary"
              track-id="longgrass_dispute-submit_button"
            />
          </div>
        </form>
      </main>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { LsSelect, LsButton, LsInput, LsInfoCard } from '@/components/common';
import { LsSupportHeader } from '@/views/Authorized/SupportView/components';
import { themedComponentMixin } from '@/mixins';
import { mapState, mapActions } from 'vuex';
import {
  CUSTOMER_UNAWARE_OF_LG_POLICY,
  GRASS_NOT_OVER_6IN,
  GRASS_NOT_OVER_9IN,
  SERVICE_DELAYS_LED_TALL_GRASS,
  longGrassDisputeReasons,
} from 'ls-constants';
import { LONG_GRASS__GET, LONG_GRASS__SUBMIT_DISPUTE } from '@/store/types';
import _ from 'lodash';

export default {
  name: 'LongGrassDisputeView',
  components: { LsSupportHeader, LsSelect, LsButton, LsInput, LsInfoCard },
  mixins: [themedComponentMixin],
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    return {
      form: {
        reason: null,
        reasonDetails: null,
        reasonComments: '',
      },
      isReturning: false,
    };
  },
  computed: {
    ...mapState(['longGrass']),
    propertyId() {
      return this.$route.params.property_id;
    },
    reportId() {
      return this.$route.params.report_id;
    },
    scheduleId() {
      return this.$route.params.schedule_id;
    },
    scheduleEventId() {
      return this.$route.params.scheduleevent_id;
    },
    completedOn() {
      return moment(this.longGrass.scheduleEventStatusSetAt).format('ddd, MMM Do');
    },
    disputeReasons() {
      return longGrassDisputeReasons;
    },
    disputeReasonsList() {
      return this.disputeReasons.map((option) => (option.reason ? option.reason : option));
    },
    disputeReasonDetailsList() {
      const target = this.disputeReasons.find(
        (option) => !_.isUndefined(option.reason) && option.reason.value === this.form.reason
      );

      return target ? target.details : [];
    },
    disputeReasonDetailsLabel() {
      switch (this.form.reason) {
        case GRASS_NOT_OVER_6IN:
          return 'How much of the service area was under 6″ in grass height?';
        case GRASS_NOT_OVER_9IN:
          return 'How much of the service area was under 9″ in grass height?';
        case SERVICE_DELAYS_LED_TALL_GRASS:
          return 'Why was there a service delay?';
        default:
          return null;
      }
    },
    isReasonCustomerUnawareOfPolicy() {
      return this.form.reason === CUSTOMER_UNAWARE_OF_LG_POLICY;
    },
    selectedReason() {
      return this.form.reason;
    },
    showDetailsTextarea() {
      switch (this.form.reason) {
        case GRASS_NOT_OVER_6IN:
        case SERVICE_DELAYS_LED_TALL_GRASS:
          return this.form.reasonDetails !== null;
        case CUSTOMER_UNAWARE_OF_LG_POLICY:
        case null:
          return false;
        default:
          return true;
      }
    },
    showSubmitButton() {
      return this.form.reason !== CUSTOMER_UNAWARE_OF_LG_POLICY;
    },
    additionalDetailsRequired() {
      return !_.includes([GRASS_NOT_OVER_6IN, SERVICE_DELAYS_LED_TALL_GRASS], this.form.reason);
    },
  },
  watch: {
    selectedReason: function() {
      if (this.isReturning) {
        this.isReturning = false;
        return;
      }
      this.form.reasonDetails = null;
      this.form.reasonComments = '';
    },
  },
  created() {
    const { form, isBack } = this.$route.params;
    this.isReturning = !!isBack;
    if (form) {
      this.form.reason = form.reason;
      this.form.reasonDetails = form.reasonDetails;
      this.form.reasonComments = form.reasonComments;
    }
  },
  async beforeMount() {
    if (this.longGrass.id) {
      return;
    }
    const { propertyId, reportId, scheduleId, scheduleEventId } = this;
    await this[LONG_GRASS__GET]({ propertyId, reportId, scheduleId, scheduleEventId });
  },
  methods: {
    ...mapActions([LONG_GRASS__SUBMIT_DISPUTE, LONG_GRASS__GET]),
    async submitForm() {
      const valid = await this.$validator.validateAll();

      if (!valid) {
        return;
      }

      const {
        form: { reason, reasonDetails, reasonComments },
        propertyId,
        scheduleId,
        scheduleEventId,
        reportId,
      } = this;

      const reportInfo = {
        reason,
        ...(reasonDetails ? { reason_details: reasonDetails } : {}),
        ...(reasonComments ? { notes: reasonComments } : {}),
        propertyId,
        scheduleId,
        scheduleEventId,
        longgrassId: reportId,
      };

      const success = await this[LONG_GRASS__SUBMIT_DISPUTE](reportInfo);

      if (success) {
        this.$router.push({ name: 'LongGrassDisputeSuccess' });
        return;
      }
      this.$router.push({ name: 'LongGrassDisputeFail', params: { form: this.form } });
    },
    goToReport() {
      this.$router.push({ name: 'LongGrassReport' });
    },
  },
};
</script>
<style lang="scss" scoped>
.long-grass-dispute {
  form {
    .ctrl-group {
      margin-top: 2rem;
    }
  }

  .info-card {
    padding: 2rem 4rem;
    margin: 2rem 0;
    flex-direction: column;
    align-items: flex-start;

    p {
      margin: 0.5rem 0;
    }
  }
}
</style>
